import { useCallback } from 'react';

interface UseWizardNavigationOptions {
  totalSteps: number;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  isStepSkipped?: (stepIndex: number) => boolean;
  beforeNext?: () => Promise<boolean>;
}

interface UseWizardNavigationResult {
  goToStep: (step: number) => void;
  nextStep: () => Promise<boolean>;
  prevStep: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export function useWizardNavigation({
  totalSteps,
  currentStep,
  setCurrentStep,
  isStepSkipped,
  beforeNext,
}: UseWizardNavigationOptions): UseWizardNavigationResult {
  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setCurrentStep(step);
      }
    },
    [totalSteps, setCurrentStep]
  );

  const nextStep = useCallback(async (): Promise<boolean> => {
    // Run pre-navigation hook (validation, duplicate check, etc.)
    if (beforeNext) {
      const canProceed = await beforeNext();
      if (!canProceed) return false;
    }

    // Find the next non-skipped step
    let next = currentStep + 1;
    while (next < totalSteps && isStepSkipped?.(next)) {
      next++;
    }

    if (next >= totalSteps) return false;

    setCurrentStep(next);
    return true;
  }, [currentStep, totalSteps, setCurrentStep, isStepSkipped, beforeNext]);

  const prevStep = useCallback(() => {
    // Find the previous non-skipped step
    let prev = currentStep - 1;
    while (prev >= 0 && isStepSkipped?.(prev)) {
      prev--;
    }

    if (prev < 0) return;

    setCurrentStep(prev);
  }, [currentStep, setCurrentStep, isStepSkipped]);

  return {
    goToStep,
    nextStep,
    prevStep,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === totalSteps - 1,
  };
}
